import { useLocation } from '@solidjs/router';
import type { JSX } from 'solid-js';
import { t } from '../i18n';
import { currentRailItem, filterRailGroupsByScope, NAV, ORG_RAIL } from '../lib/rail';
import {
  OWED_COUNT_KEYS,
  type RailCounts,
  type RailDots,
  useRailCounts,
  useRailDots,
} from '../lib/rail-counts';
import { SESSION_TOKEN } from '../lib/session';
import type { AppNavItem } from './AppNav';
import { RailGroups, type RailSection } from './RailGroups';

// The org rail: the five job groups — Orient, Watch, Decide, Prove & configure,
// Settings — every one foldable (issue #5685). Container: it owns the rail's
// reads (the row counts + the Terminal status dot), the lit row and the fold
// store handoff; `RailGroups` renders them under the ONE `<nav>` landmark. Split
// from `AppSidebar` so the admin host, which renders no org rail, never fires
// the org count/dot reads either.
//
// The caller's OWN scope list (`/v1/me`) gates the ROWS: the personal-key and
// GitHub-token rows are dropped before the badges are attached, so a
// credential without `write:self:keys` is offered neither. The lit row still
// resolves against the FULL `NAV` — a page is reachable by URL whatever the
// rail offers.

const withBadges = (
  items: readonly AppNavItem[],
  counts: RailCounts,
  dots: RailDots,
): readonly AppNavItem[] =>
  items.map((item) => {
    const count = counts[item.key];
    const dot = dots[item.key];
    return {
      ...item,
      ...(count === undefined ? {} : { count }),
      ...(dot === undefined ? {} : { dot }),
    };
  });

export function OrgRail(props: {
  readonly resolve: (href: string) => string;
  /**
   * The caller's own scope list (`/v1/me`), or `undefined` when it publishes
   * none — an absent list keeps every row (fail open).
   */
  readonly scopes?: readonly string[] | undefined;
  /** Test seam: the bearer the count/dot reads use. The app passes nothing. */
  readonly token?: string | null;
  /** The collapsed icon rail (#7710): every section open, folds untouched. */
  readonly collapsed?: boolean | undefined;
}): JSX.Element {
  const location = useLocation();
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const counts = useRailCounts(token);
  const dots = useRailDots(token);
  const sections = (): readonly RailSection[] =>
    filterRailGroupsByScope(ORG_RAIL, props.scopes).map((group) => ({
      ...group,
      items: withBadges(group.items, counts, dots),
    }));
  return (
    <RailGroups
      label={t('nav.primary')}
      groups={sections()}
      resolve={props.resolve}
      current={currentRailItem(location.pathname, NAV, props.resolve)?.href}
      owed={OWED_COUNT_KEYS}
      collapsed={props.collapsed}
    />
  );
}
